import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/shadcn/input'
import { Button } from '@/components/shadcn/button'
import { Label } from '@/components/shadcn/label'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Separator } from '@/components/shadcn/separator'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import {
  usePatchApiCompaniesCurrentActionmodulesActionModuleId,
  usePatchApiCompaniesCurrentActionmodulesActionModuleIdIcon,
} from '@/api/generated/company-management-user/company-management-user'
import { notify } from '@/lib/notify'
import type { ActionModule } from '../types'
import { LocalizationEntityType } from '@/api/model/localizationEntityType'
import { IconColorPicker } from '@/components/common/IconColorPicker'
import { TranslateButton } from '@/components/common/translations/TranslateButton'
import { useEntityTranslations } from '@/components/common/translations/useEntityTranslations'
import { RichTextEditor } from '@/components/common/RichTextEditor'

const TRANSLATABLE_PROPERTIES = ['name', 'description', 'employeeDescription', 'completionDescriptionText']

export function EditDialog({
  module,
  onClose,
  onSaved,
}: {
  module: ActionModule
  onClose: () => void
  onSaved: () => void
}) {
  const {t} = useTranslation(['action-modules', 'common'])
  const [name, setName] = useState(module.name ?? '')
  const [description, setDescription] = useState(module.description ?? '')
  const [employeeDescription, setEmployeeDescription] = useState(module.employeeDescription ?? '')
  const [completionText, setCompletionText] = useState(module.completionDescriptionText ?? '')
  const [colorHex, setColorHex] = useState(module.colorHex ?? '#4ca7aa')
  const [iconName, setIconName] = useState(module.iconName ?? null)
  const [iconUrl, setIconUrl] = useState(module.iconUrl ?? null)
  const [maxReward] = useState(module.maxReward)
  const [maxUserCanComplete, setMaxUserCanComplete] = useState<number | null>(module.maxUserCanComplete)
  const [noLimit, setNoLimit] = useState(module.maxUserCanComplete === null)
  const [emailAddresses, setEmailAddresses] = useState<string[]>(
    (module.emailNotificationAddresses ?? []).filter(Boolean)
  )
  const [summaryEmail, setSummaryEmail] = useState(module.summaryEmailNotification ?? false)
  const [detailedEmail, setDetailedEmail] = useState(module.detailedEmailNotification ?? false)

  const patch = usePatchApiCompaniesCurrentActionmodulesActionModuleId()
  const patchIcon = usePatchApiCompaniesCurrentActionmodulesActionModuleIdIcon()
  const translations = useEntityTranslations({
    entityType: LocalizationEntityType.ActionModule,
    entityId: String(module.id),
    properties: TRANSLATABLE_PROPERTIES,
  })

  // Image upload goes through a dedicated endpoint and takes effect immediately
  // (not only on save). Backend expects the file as multipart FormData (`File`);
  // sending `File: null` clears/removes the icon.
  async function handleUpload(file: File, dataUrl: string) {
    try {
      await patchIcon.mutateAsync({actionModuleId: module.id!, data: {File: file}})
      setIconUrl(dataUrl)
      onSaved()
    } catch (error) {
      notify.apiError(error, t('toasts.uploadError'))
    }
  }

  async function handleDeleteIcon() {
    try {
      await patchIcon.mutateAsync({actionModuleId: module.id!, data: {File: undefined}})
      setIconUrl(null)
      onSaved()
    } catch (error) {
      notify.apiError(error, t('toasts.removeIconError'))
    }
  }

  function addEmail() {
    setEmailAddresses(p => [...p, ''])
  }

  function removeEmail(i: number) {
    setEmailAddresses(p => p.filter((_, idx) => idx !== i))
  }

  function setEmail(i: number, v: string) {
    setEmailAddresses(p => p.map((e, idx) => idx === i ? v : e))
  }

  async function handleSave() {
    try {
      await patch.mutateAsync({
        actionModuleId: module.id!,
        data: {
          name: name || null,
          description: description || null,
          employeeDescription: employeeDescription || null,
          completionDescriptionText: completionText || null,
          // Not editable in the dialog -> pass existing values through, don't null them.
          positiveAnswerText: module.positiveAnswerText ?? null,
          negativeAnswerText: module.negativeAnswerText ?? null,
          iconName: iconName || null,
          colorHex: colorHex || null,
          maxReward,
          rewardType: module.rewardType,
          maxUserCanComplete: noLimit ? null : maxUserCanComplete,
          emailNotificationAddresses: emailAddresses.filter(Boolean),
          summaryEmailNotification: summaryEmail,
          detailedEmailNotification: detailedEmail,
          // Assignment not editable in the dialog (only effective in the full-update path) ->
          // pass existing values through. Required field in the request.
          isCompanyWideAssigned: module.isCompanyWideAssigned ?? true,
          canBeEdited: module.canBeEdited ?? true,
          // Not editable in the dialog -> pass through, else the backend resets them to false.
          allowSaveDraft: module.allowSaveDraft ?? false,
          showInEmployeeDashboard: module.showInEmployeeDashboard ?? false,
          assignedGroupIds: module.assignedGroupIds ?? [],
        },
      })
      await translations.save(String(module.id))
      notify.success(t('toasts.saved'))
      onSaved()
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.saveError'))
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl! max-h-[85vh] flex! flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {t('editDialog.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 gap-6 overflow-y-auto py-2">
          {/* Column 1: symbol, users, emails, visibility */}
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {/* Symbol: image OR icon+color */}
            <IconColorPicker
              iconName={iconName}
              colorHex={colorHex}
              iconUrl={iconUrl}
              busy={patchIcon.isPending}
              onIconChange={setIconName}
              onColorChange={setColorHex}
              onUpload={handleUpload}
              onDelete={handleDeleteIcon}
            />

            {/* Max. Nutzer + Erreichbare Punkte (read-only) + Benachrichtigung */}
            <div className="flex items-start gap-6">
              <div className="flex flex-col gap-1.5">
                <Label>{t('editDialog.reachablePoints')}</Label>
                <Input type="number" disabled className="w-28 h-8" value={maxReward}/>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label>{t('editDialog.notification')}</Label>
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                    <Checkbox id="summary-email" checked={summaryEmail} onCheckedChange={v => setSummaryEmail(!!v)}/>
                    <label htmlFor="summary-email"
                           className="text-sm cursor-pointer">{t('editDialog.summaryEmail')}</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="detailed-email" checked={detailedEmail} onCheckedChange={v => setDetailedEmail(!!v)}/>
                    <label htmlFor="detailed-email"
                           className="text-sm cursor-pointer">{t('editDialog.detailedEmail')}</label>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('editDialog.maxUsers')}</Label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Checkbox id="no-limit" checked={noLimit} onCheckedChange={v => {
                      setNoLimit(!!v);
                      if (v) setMaxUserCanComplete(null)
                    }}/>
                    <label htmlFor="no-limit" className="text-sm cursor-pointer">{t('editDialog.noLimit')}</label>
                  </div>
                  {!noLimit && (
                    <Input
                      type="number"
                      min={1}
                      className="w-20 h-8"
                      value={maxUserCanComplete ?? 1}
                      onChange={e => setMaxUserCanComplete(Number(e.target.value))}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Email notifications */}
            <div className="flex flex-col gap-1.5">
              <Label>{t('editDialog.emailNotifications')}</Label>
              <div className="flex flex-col gap-2">
                {emailAddresses.map((email, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      type="email"
                      value={email}
                      onChange={e => setEmail(i, e.target.value)}
                      placeholder={t('editDialog.emailPlaceholder')}
                      className="h-8 text-sm"
                    />
                    <Button variant="ghost" size="icon" className="size-8 text-destructive"
                            onClick={() => removeEmail(i)}>
                      ✕
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="w-fit h-8 text-xs" onClick={addEmail}>
                  + {t('editDialog.addEmail')}
                </Button>
              </div>
            </div>
          </div>

          <Separator orientation="vertical"/>

          {/* Column 2: texts */}
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="am-name">{t('editDialog.moduleName')}</Label>
              <div className="flex gap-2">
                <Input id="am-name" value={name} onChange={e => setName(e.target.value)}
                       placeholder={t('editDialog.moduleNamePlaceholder')}/>
                <TranslateButton
                  dialogTitle={t('editDialog.moduleNameTranslateTitle')}
                  value={translations.get('name')}
                  onChange={v => translations.set('name', v)}
                  sourceValue={name}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="am-description">{t('editDialog.moduleDescription')}</Label>
              <div className="flex items-start gap-2">
                <RichTextEditor
                  id="am-description"
                  value={description}
                  onChange={setDescription}
                  className="min-w-0 flex-1"
                />
                <TranslateButton
                  dialogTitle={t('editDialog.moduleDescriptionTranslateTitle')}
                  value={translations.get('description')}
                  onChange={v => translations.set('description', v)}
                  sourceValue={description}
                  isRichText
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="am-employee-description">{t('editDialog.employeeDescription')}</Label>
              <div className="flex items-start gap-2">
                <RichTextEditor
                  id="am-employee-description"
                  value={employeeDescription}
                  onChange={setEmployeeDescription}
                  className="min-w-0 flex-1"
                />
                <TranslateButton
                  dialogTitle={t('editDialog.employeeDescriptionTranslateTitle')}
                  value={translations.get('employeeDescription')}
                  onChange={v => translations.set('employeeDescription', v)}
                  sourceValue={employeeDescription}
                  isRichText
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="am-completion-text">{t('editDialog.completionText')}</Label>
              <div className="flex items-start gap-2">
                <RichTextEditor
                  id="am-completion-text"
                  value={completionText}
                  onChange={setCompletionText}
                  className="min-w-0 flex-1"
                />
                <TranslateButton
                  dialogTitle={t('editDialog.completionTextTranslateTitle')}
                  value={translations.get('completionDescriptionText')}
                  onChange={v => translations.set('completionDescriptionText', v)}
                  sourceValue={completionText}
                  isRichText
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('buttons.cancel', {ns: 'common'})}</Button>
          <Button onClick={handleSave} disabled={patch.isPending}>{t('buttons.save', {ns: 'common'})}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
